import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getRewardsStatus,
  getRewardsOverview,
  getMyReferrals,
  updateCustomSlug,
  type ReferralStatus,
} from "@/app/_module/services/referralService";
import { isDemoMode } from "@/app/_module/utils/demoMode";

const REWARDS_KEYS = {
  all: ["rewards"] as const,
  status: () => [...REWARDS_KEYS.all, "status"] as const,
  overview: () => [...REWARDS_KEYS.all, "overview"] as const,
  referrals: (status?: ReferralStatus) =>
    [...REWARDS_KEYS.all, "referrals", status] as const,
};

export function useRewardsStatus() {
  return useQuery({
    queryKey: REWARDS_KEYS.status(),
    queryFn: getRewardsStatus,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

/**
 * DEMO SANDBOX: a populated rewards overview.
 *
 * A demo seat never actually joined the programme, so the real endpoint returns nothing
 * and the page falls through to its empty state — the visitor sees the feature exists but
 * not what it looks like working. These numbers put a mid-tier agent on screen: enough
 * conversions that the tier bar has somewhere to go, not so many it looks invented.
 *
 * The referral link is a fixed demo slug rather than the seat's real code. Seats are
 * recycled and a slug is globally unique, so surfacing a real one would show the same
 * link to every visitor and risk colliding with an actual agent's.
 */
const DEMO_REWARDS_OVERVIEW = {
  code: 'DEMO2026',
  customSlug: 'your-agency',
  referralUrl: 'https://app.insuracentral.com/r/your-agency',
  stats: {
    totalClicks: 214,
    totalSignups: 26,
    totalConversions: 9,
  },
  rewards: {
    freeMonthsEarned: 9,
    freeMonthsRedeemed: 4,
    freeMonthsAvailable: 5,
  },
};

export function useRewardsOverview(enabled = true) {
  return useQuery({
    queryKey: REWARDS_KEYS.overview(),
    queryFn: isDemoMode()
      ? async () => DEMO_REWARDS_OVERVIEW as any
      : getRewardsOverview,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
    enabled,
  });
}

/**
 * DEMO SANDBOX: a believable referral history to sit under the stats above.
 *
 * Counts alone leave the history table empty, which reads as the feature half-working.
 * The mix mirrors the funnel in DEMO_REWARDS_OVERVIEW — more clicks than signups, more
 * signups than conversions — and dates are relative to NOW() so it never looks stale.
 */
const DEMO_REFERRALS = (() => {
  const daysAgo = (d: number) => new Date(Date.now() - d * 86400000).toISOString();
  const rows: Array<Record<string, unknown>> = [
    ['Marcus Webb', 'converted', 4],
    ['Alicia Tran', 'converted', 11],
    ['Roy Castellano', 'trial', 16],
    ['Dana Whitfield', 'signed_up', 23],
    ['Priya Raman', 'converted', 29],
    ['Terrence Boyd', 'clicked', 34],
    ['Sofia Marquez', 'signed_up', 41],
    ['Elliot Nash', 'churned', 52],
  ].map(([name, status, d]: any, i: number) => ({
    id: `demo-ref-${i + 1}`,
    status,
    // The table renders firstName/lastName, not a combined name — a `name` field alone
    // would have produced eight blank rows.
    referredUser: {
      firstName: String(name).split(' ')[0],
      lastName: String(name).split(' ').slice(1).join(' '),
      email: `${String(name).toLowerCase().replace(/\s+/g, '.')}@example.com`,
    },
    clickedAt: daysAgo(d + 2),
    signedUpAt: status === 'clicked' ? null : daysAgo(d + 1),
    convertedAt: status === 'converted' ? daysAgo(d) : null,
    createdAt: daysAgo(d + 2),
  }));
  return { referrals: rows, total: rows.length };
})();

export function useMyReferrals(params?: {
  status?: ReferralStatus;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: REWARDS_KEYS.referrals(params?.status),
    queryFn: isDemoMode()
      ? async () => DEMO_REFERRALS as any
      : () => getMyReferrals(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

export function useUpdateCustomSlug() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => updateCustomSlug(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REWARDS_KEYS.overview() });
    },
  });
}
